import React, { FC, useState } from 'react';
import { Box, Checkbox, CircularProgress, FormControlLabel, IconButton, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { formatFilmDayList, toDayList } from '../../../utils/filmWindow';
import { ReactComponent as LinkedinIcon } from '../../../assets/svg/linkedin.svg';
import { getBpp, ProDecision } from '../helper';
import { ExpertIdPill } from '../../../components/ExpertIdPill/ExpertIdPill';
import { cldUrl } from '../../../utils/cloudinary';

const getInitials = (name?: string) =>
  name ? name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() : '?';

const AVATAR_COLORS = ['#AB854A', '#7B8F6B', '#8B7BA5', '#6B8F8F', '#A57B6B', '#6B7BA5'];

const cardOpacity = (disabled: boolean, locked: boolean) => {
  if (disabled) return 0.5;
  return locked ? 0.85 : 1;
};

const viewDetailsButton = {
  width: 26, height: 26, p: 0, borderRadius: '50%',
  border: '1px solid var(--border)', backgroundColor: 'var(--surface)', color: 'var(--muted)',
  '&:hover': { borderColor: 'var(--accent)', color: 'var(--accent-text)', backgroundColor: 'var(--tint)' },
};

const cardBase = (active: boolean, locked: boolean, disabled: boolean) => ({
  display: 'flex', gap: 1.5, p: 2, mb: 1.5, borderRadius: '14px',
  cursor: locked || disabled ? 'default' : 'pointer',
  border: active ? '1.5px dashed #D4B888' : '1.5px solid var(--border)',
  backgroundColor: active ? 'var(--tint)' : 'var(--surface)',
  transition: 'all 0.15s',
  '&:hover': locked || disabled ? {} : { borderColor: '#D4B888' },
});

const BADGE_BORDERS: Record<string, string> = { 'var(--up)': '#A5D6A7', 'var(--down)': '#FFCDD2' };

const badge = (color: string, bg?: string) => ({
  fontSize: 10, fontWeight: 600, color, borderRadius: '10px', px: 0.8, py: 0.1, lineHeight: '16px',
  ...(bg ? { backgroundColor: bg } : {
    border: '1px solid',
    // Light keeps its historical pastels. On the dark canvas those pale tints
    // ring the chip BRIGHTER than the text they frame, so dark just follows the
    // chip's own (tokenized) text color via currentColor — no new literals.
    borderColor: BADGE_BORDERS[color] || '#E4CBA8',
    '[data-theme="dark"] &': { borderColor: 'currentColor' },
  }),
});

const checkCircle = (active: boolean) => ({
  width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'all 0.15s',
  ...(active
    ? { backgroundColor: 'var(--accent)' }
    : {
        // --border is a hairline-RULE token: rgba(255,255,255,.08) over
        // --surface is ~1.15:1, which erases this control's only affordance.
        // Light keeps the historical hairline; dark needs a real outline.
        border: '2px solid var(--border)',
        '[data-theme="dark"] &': { borderColor: 'var(--muted)' },
      }),
});

const ExpertAvatar: FC<{ expert: any }> = ({ expert }) =>
  expert.avatar ? (
    <Box component="img" src={cldUrl(expert.avatar, { w: 88, h: 88, crop: 'fill', gravity: 'face' })} alt={expert.displayName}
      sx={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
  ) : (
    <Box sx={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
      backgroundColor: AVATAR_COLORS[expert.userid % AVATAR_COLORS.length],
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700 }}>
      {getInitials(expert.displayName)}
    </Box>
  );

const ExpertBadges: FC<{ score: any; status: string; userid: number; hasLinkedin: boolean; decision: ProDecision; followUpCount: number }>
  = ({ score, status, userid, hasLinkedin, decision, followUpCount }) => (
  <>
    {/* No `border` override here: re-assigning a spread key keeps its ORIGINAL
        position, so badge()'s borderColor is still emitted after it and wins.
        The override was dead before the theme work too (it was #D4D4D4), so
        this badge has always worn badge()'s outline. */}
    {score != null && <Box sx={badge('var(--text-2)')}>{parseFloat(score)}%</Box>}
    {hasLinkedin && <LinkedinIcon width={14} height={14} />}
    <Box sx={badge(status === 'ACTIVE' ? 'var(--up)' : 'var(--accent-text)')}>{status}</Box>
    <ExpertIdPill userid={userid} />
    {decision === 'opted_in' && <Box sx={badge('var(--up)')}>OPTED IN</Box>}
    {decision === 'opted_out' && <Box sx={badge('var(--down)')}>OPTED OUT</Box>}
    {decision === 'notified' && <Box sx={badge('var(--accent-text)', 'var(--tint)')}>NOTIFIED</Box>}
    {decision === 'notified' && followUpCount > 0 && (
      <Box
        title={`${followUpCount} follow-up${followUpCount > 1 ? 's' : ''} sent`}
        sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', ml: 0.2 }}
      >
        <NotificationsActiveOutlinedIcon sx={{ fontSize: 16, color: 'var(--accent-text)' }} />
        <Box
          sx={{
            position: 'absolute', top: -5, right: -6,
            minWidth: 14, height: 14, px: '3px',
            borderRadius: '7px', backgroundColor: 'var(--accent)', color: '#fff',
            fontSize: 9, fontWeight: 700, lineHeight: '14px', textAlign: 'center',
          }}
        >
          {followUpCount}
        </Box>
      </Box>
    )}
  </>
);

interface ExpertConfirmCardProps {
  expert: any;
  isSelected: boolean;
  isLocked?: boolean;
  decision?: ProDecision;
  onToggle: () => void;
  preferredDates?: string[] | null;
  userConfirmed?: boolean | null;
  // Active SOW cycle statuses (expert envelope / company countersign) — the
  // contract sent when the team confirmed this expert.
  sowStatus?: string | null;
  countersignStatus?: string | null;
  // The cycle was recorded as already signed at internal confirm (no
  // envelope — a reshoot covered by an earlier project's SOW, say).
  sowOnFile?: boolean;
  // "SOW already signed" checkbox, offered while the expert is not yet
  // confirmed: confirming with it ticked records the SOW instead of sending
  // one. Parents keep the state; the card only renders it when given a handler.
  alreadySignedChecked?: boolean;
  onAlreadySignedChange?: (checked: boolean) => void;
  disabled?: boolean;
  onViewDetails?: () => void;
  viewDetailsHint?: string;
  // Downloads the confirmation details PDF (+ video/photos zip); parents
  // pass it only for roles allowed to export (canDownloadConfirmation).
  onDownloadDetails?: () => Promise<void>;
}

// SOW progress badge for a confirmed expert (mirrors the SOW pills on the
// Expert Selection tab, where the countersign action lives).
const sowBadge = (sowStatus?: string | null, countersignStatus?: string | null, sowOnFile = false) => {
  if (sowOnFile) return { label: 'SOW ON FILE', color: 'var(--up)' };
  if (!sowStatus) return null;
  if (sowStatus === 'sent') return { label: 'SOW SENT', color: 'var(--accent-text)', bg: 'var(--tint)' };
  if (sowStatus === 'declined') return { label: 'SOW DECLINED', color: 'var(--down)' };
  if (sowStatus === 'voided') return { label: 'SOW VOIDED', color: 'var(--down)' };
  if (sowStatus === 'completed') {
    if (countersignStatus === 'completed') return { label: 'SOW EXECUTED', color: 'var(--up)' };
    if (countersignStatus === 'declined') return { label: 'COUNTERSIGN DECLINED', color: 'var(--down)' };
    if (countersignStatus === 'voided') return { label: 'COUNTERSIGN VOIDED', color: 'var(--down)' };
    return { label: 'SOW SIGNED · COUNTERSIGN', color: 'var(--accent-text)', bg: 'var(--tint)' };
  }
  return null;
};

export const ExpertConfirmCard: FC<ExpertConfirmCardProps> = ({
  expert,
  isSelected,
  isLocked = false,
  decision = null,
  onToggle,
  preferredDates = null,
  userConfirmed,
  sowStatus = null,
  countersignStatus = null,
  sowOnFile = false,
  alreadySignedChecked = false,
  onAlreadySignedChange,
  disabled = false,
  onViewDetails,
  viewDetailsHint = 'Open confirmation details',
  onDownloadDetails,
}) => {
  const [downloading, setDownloading] = useState(false);
  const professions = expert.professions?.map((p: any) => p.profession?.name).filter(Boolean).join(' | ') || '';
  const bpp = getBpp(expert);
  const score = bpp?.score;
  const followUpCount = bpp?.follow_up_count || 0;
  const bio = expert.short_description || '';
  const status = expert.usertype === 'verified creator' ? 'ACTIVE' : 'PROSPECT';
  const active = isSelected || isLocked;
  const optedOut = decision === 'opted_out';
  const dateAnswer = optedOut ? null : userConfirmed;
  const preferredLabel = (() => {
    const days = formatFilmDayList(toDayList(preferredDates));
    return days ? `Prefers ${days}` : null;
  })();
  const awaitingDate = !optedOut && dateAnswer == null && !!preferredLabel;
  const dimmed = { opacity: cardOpacity(disabled, isLocked) };
  const sow = isSelected ? sowBadge(sowStatus, countersignStatus, sowOnFile) : null;
  const showAlreadySigned = !isSelected && !isLocked && !!onAlreadySignedChange;
  const needsCountersign = !!sow && sow.label === 'SOW SIGNED · COUNTERSIGN';

  return (
    <Box
      onClick={() => !isLocked && !disabled && onToggle()}
      sx={cardBase(active, isLocked, disabled)}
    >
      <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0, ...dimmed }}>
        <ExpertAvatar expert={expert} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }} noWrap>{expert.displayName || 'Unknown'}</Typography>
            <ExpertBadges score={score} status={status} userid={expert.userid} hasLinkedin={!!expert.linkedin} decision={decision} followUpCount={followUpCount} />
          </Box>
          {professions && <Typography sx={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.3 }} noWrap>{professions}</Typography>}
          {(preferredLabel || dateAnswer != null || sow) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
              {preferredLabel && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <EventOutlinedIcon sx={{ fontSize: 13, color: 'var(--accent-text)' }} />
                  <Typography sx={{ fontSize: 11.5, color: 'var(--text-2)', fontWeight: 600 }}>
                    {preferredLabel}
                  </Typography>
                </Box>
              )}
              {dateAnswer === true && <Box sx={badge('var(--up)')}>DATE CONFIRMED</Box>}
              {dateAnswer === false && <Box sx={badge('var(--down)')}>DATE DECLINED</Box>}
              {awaitingDate && (
                <Box sx={badge('var(--accent-text)', 'var(--tint)')}>AWAITING DATE CONFIRMATION</Box>
              )}
              {sow && <Box sx={badge(sow.color, sow.bg)}>{sow.label}</Box>}
            </Box>
          )}
          {needsCountersign && (
            <Typography sx={{ fontSize: 11.5, color: 'var(--accent-text)', mt: 0.5 }}>
              Countersign from this expert&apos;s card on the Expert Selection tab.
            </Typography>
          )}
          {showAlreadySigned && (
            <Box sx={{ mt: 0.5 }} onClick={(event) => event.stopPropagation()}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={alreadySignedChecked}
                    disabled={disabled}
                    onChange={(event) => onAlreadySignedChange?.(event.target.checked)}
                    inputProps={{ 'aria-label': `SOW already signed for ${expert.displayName || 'expert'}` }}
                    sx={{ p: 0.4, color: 'var(--muted)', '&.Mui-checked': { color: 'var(--accent)' } }}
                  />
                }
                label="SOW already signed"
                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12, fontWeight: 600, color: 'var(--text)' } }}
              />
              <Typography sx={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.3, pl: 3.2 }}>
                Confirming won&apos;t send a new SOW — e.g. a reshoot covered by an earlier project&apos;s SOW.
              </Typography>
            </Box>
          )}
          {bio && (
            <Typography sx={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.4, mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{bio}</Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, pt: 0.5, flexShrink: 0 }}>
        {onDownloadDetails && (
          <Tooltip title="Download confirmation details">
            <IconButton
              size="small"
              aria-label="Download confirmation details"
              disabled={downloading}
              onClick={(event) => {
                event.stopPropagation();
                setDownloading(true);
                onDownloadDetails().finally(() => setDownloading(false));
              }}
              sx={viewDetailsButton}
            >
              {downloading ? <CircularProgress size={11} sx={{ color: 'inherit' }} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Tooltip>
        )}
        {onViewDetails && (
          <Tooltip title={viewDetailsHint}>
            <IconButton
              size="small"
              aria-label={viewDetailsHint}
              onClick={(event) => { event.stopPropagation(); onViewDetails(); }}
              sx={viewDetailsButton}
            >
              <OpenInNewOutlinedIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ ...checkCircle(active), ...dimmed }}>{active && <CheckIcon sx={{ color: '#fff', fontSize: 14 }} />}</Box>
      </Box>
    </Box>
  );
};

export default ExpertConfirmCard;
